import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, RefreshCw, BookOpen, Calendar, GraduationCap } from 'lucide-react';
import axios from 'axios';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    duration_years: 4,
    exam_pattern: 'semester',
    lateral_entry_allowed: false,
    lateral_entry_semester: 3,
    description: '',
    auto_create_terms: true
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  // Auto-generate code from course name
  const generateCode = (name) => {
    if (!name) return '';
    const stopWords = ['of', 'and', 'the', 'in', 'for', 'to', 'a', 'an', 'bachelor', 'master', 'diploma', 'degree'];
    const words = name.split(' ').filter(word => !stopWords.includes(word.toLowerCase()));
    
    if (words.length === 1) {
      return words[0].substring(0, 3).toUpperCase();
    } else {
      return words.map(word => word.charAt(0).toUpperCase()).join('');
    }
  };

  // Handle name change and auto-generate code
  const handleNameChange = (name) => {
    if (editingCourse) {
      setFormData({ ...formData, name });
    } else {
      const newCode = generateCode(name);
      setFormData({ ...formData, name, code: newCode });
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Auto-create semesters/years for a course
  const autoCreateTerms = async (courseId, courseCode, durationYears, examPattern, lateralEntryAllowed, lateralEntrySemester) => {
    try {
      const totalTerms = examPattern === 'semester' ? durationYears * 2 : durationYears;
      const termName = examPattern === 'semester' ? 'Semester' : 'Year';
      
      for (let i = 1; i <= totalTerms; i++) {
        // Determine entry_type based on lateral entry settings
        let entryType = 'regular';
        if (lateralEntryAllowed && i >= lateralEntrySemester) {
          entryType = 'both'; // Both regular and lateral students can be in this semester
        }
        
        await axios.post(`${API_URL}/api/master-settings/semesters`, {
          name: `${termName} ${i}`,
          course_id: courseId,
          semester_number: i,
          entry_type: entryType
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      return true;
    } catch (error) {
      console.error('Error auto-creating terms:', error);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Validate course code - no dots allowed
    if (formData.code && formData.code.includes('.')) {
      setError('Course Code cannot contain DOT (.). Example: Use BED instead of B.ED');
      setLoading(false);
      return;
    }
    
    try {
      const submitData = {
        name: formData.name,
        code: formData.code,
        duration_years: formData.duration_years,
        exam_pattern: formData.exam_pattern,
        lateral_entry_allowed: formData.lateral_entry_allowed,
        lateral_entry_semester: formData.lateral_entry_semester,
        description: formData.description
      };

      if (editingCourse) {
        await axios.put(`${API_URL}/api/master-settings/courses/${editingCourse.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const response = await axios.post(`${API_URL}/api/master-settings/courses`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Auto-create semesters/years if checked
        if (formData.auto_create_terms && response.data.id) {
          await autoCreateTerms(
            response.data.id, 
            formData.code, 
            formData.duration_years, 
            formData.exam_pattern,
            formData.lateral_entry_allowed,
            formData.lateral_entry_semester
          );
        }
      }
      
      fetchCourses();
      setShowModal(false);
      setFormData({ name: '', code: '', duration_years: 4, exam_pattern: 'semester', lateral_entry_allowed: false, lateral_entry_semester: 3, description: '', auto_create_terms: true });
      setEditingCourse(null);
    } catch (error) {
      console.error('Error saving course:', error);
      setError(error.response?.data?.detail || 'Error saving course');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        await axios.delete(`${API_URL}/api/master-settings/courses/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchCourses();
      } catch (error) {
        console.error('Error deleting course:', error);
      }
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name,
      code: course.code,
      duration_years: course.duration_years,
      exam_pattern: course.exam_pattern || 'semester',
      lateral_entry_allowed: course.lateral_entry_allowed || false,
      lateral_entry_semester: course.lateral_entry_semester || 3,
      description: course.description || '',
      auto_create_terms: false
    });
    setShowModal(true);
  };

  const filteredCourses = courses.filter(course =>
    course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group courses by exam pattern
  const semesterCourses = filteredCourses.filter(c => c.exam_pattern !== 'yearly');
  const yearlyCourses = filteredCourses.filter(c => c.exam_pattern === 'yearly');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
          <p className="text-sm text-gray-500 mt-1">Manage course information with exam patterns</p>
        </div>
        <button
          onClick={() => { 
            setShowModal(true); 
            setEditingCourse(null); 
            setFormData({ name: '', code: '', duration_years: 4, exam_pattern: 'semester', lateral_entry_allowed: false, lateral_entry_semester: 3, description: '', auto_create_terms: true }); 
            setError(''); 
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Course
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Semester-wise Courses */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800">Semester-wise Courses</h2>
                <p className="text-sm text-gray-600">{semesterCourses.length} courses</p>
              </div>
            </div>
          </div>
          <div className="divide-y">
            {semesterCourses.length === 0 ? (
              <p className="p-6 text-center text-gray-500">No semester courses found</p>
            ) : (
              semesterCourses.map(course => (
                <div key={course.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 px-3 py-1 rounded-lg">
                      <span className="font-bold text-blue-700">{course.code}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{course.name}</p>
                      <p className="text-xs text-gray-500">
                        {course.duration_years} Years • {course.duration_years * 2} Semesters
                        {course.lateral_entry_allowed && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                            Lateral Entry
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(course)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(course.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Yearly Courses */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="bg-green-600 p-2 rounded-lg">
                <Calendar className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800">Yearly Courses</h2>
                <p className="text-sm text-gray-600">{yearlyCourses.length} courses</p>
              </div>
            </div>
          </div>
          <div className="divide-y">
            {yearlyCourses.length === 0 ? (
              <p className="p-6 text-center text-gray-500">No yearly courses found</p>
            ) : (
              yearlyCourses.map(course => (
                <div key={course.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-green-100 px-3 py-1 rounded-lg">
                      <span className="font-bold text-green-700">{course.code}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">{course.name}</p>
                      <p className="text-xs text-gray-500">
                        {course.duration_years} Years
                        {course.lateral_entry_allowed && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                            Lateral Entry
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleEdit(course)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                      <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(course.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
          <p className="text-blue-600 text-sm">Total Courses</p>
          <p className="text-2xl font-bold text-blue-700">{courses.length}</p>
        </div>
        <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-200">
          <p className="text-indigo-600 text-sm">Semester-wise</p>
          <p className="text-2xl font-bold text-indigo-700">{semesterCourses.length}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-xl border border-green-200">
          <p className="text-green-600 text-sm">Yearly</p>
          <p className="text-2xl font-bold text-green-700">{yearlyCourses.length}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
          <p className="text-purple-600 text-sm">Active</p>
          <p className="text-2xl font-bold text-purple-700">{courses.filter(c => c.is_active).length}</p>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">{editingCourse ? 'Edit Course' : 'Add Course'}</h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Course Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Bachelor of Pharmacy"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 uppercase ${formData.code.includes('.') ? 'border-red-500 bg-red-50' : ''}`}
                    placeholder="Auto-generated"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, code: generateCode(formData.name) })}
                    className="px-3 py-2 bg-gray-100 border rounded-lg hover:bg-gray-200"
                    title="Regenerate"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Without DOT. B.ED → BED</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Years) *</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.duration_years}
                    onChange={(e) => setFormData({ ...formData, duration_years: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.exam_pattern === 'semester' ? 'Semesters' : 'Years'}
                  </label>
                  <input
                    type="text"
                    readOnly
                    value={formData.exam_pattern === 'semester' ? formData.duration_years * 2 : formData.duration_years}
                    className="w-full px-4 py-2 border rounded-lg bg-gray-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Exam Pattern *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, exam_pattern: 'semester' })}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      formData.exam_pattern === 'semester'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    <BookOpen className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-semibold text-sm">Semester</div>
                    <div className="text-xs mt-1">6 months each</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, exam_pattern: 'yearly' })}
                    className={`p-3 border-2 rounded-lg text-center transition-all ${
                      formData.exam_pattern === 'yearly'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-300 hover:border-green-300'
                    }`}
                  >
                    <Calendar className="h-5 w-5 mx-auto mb-1" />
                    <div className="font-semibold text-sm">Yearly</div>
                    <div className="text-xs mt-1">12 months each</div>
                  </button>
                </div>
              </div>

              {/* Lateral Entry Settings */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.lateral_entry_allowed}
                    onChange={(e) => setFormData({ ...formData, lateral_entry_allowed: e.target.checked })}
                    className="h-5 w-5 text-amber-600 rounded"
                  />
                  <div>
                    <span className="font-medium text-amber-800">Lateral Entry Allowed</span>
                    <p className="text-xs text-amber-600">Students can join directly in higher semester (e.g., Diploma → 2nd Year)</p>
                  </div>
                </label>
                
                {formData.lateral_entry_allowed && formData.exam_pattern === 'semester' && (
                  <div className="mt-3 ml-8">
                    <label className="block text-sm font-medium text-amber-800 mb-1">Entry Semester</label>
                    <select
                      value={formData.lateral_entry_semester}
                      onChange={(e) => setFormData({ ...formData, lateral_entry_semester: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg bg-white focus:ring-2 focus:ring-amber-500"
                    >
                      {[...Array(formData.duration_years * 2)].map((_, i) => (
                        <option key={i+1} value={i+1}>Semester {i+1}</option>
                      ))}
                    </select>
                    <p className="text-xs text-amber-600 mt-1">Lateral students will join in this semester</p>
                  </div>
                )}
              </div>

              {!editingCourse && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_create_terms}
                      onChange={(e) => setFormData({ ...formData, auto_create_terms: e.target.checked })}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm font-medium text-blue-800">
                      Auto-create {formData.exam_pattern === 'semester' ? 'Semesters' : 'Years'}
                    </span>
                  </label>
                  <p className="text-xs text-blue-600 mt-1 ml-6">
                    Will create {formData.exam_pattern === 'semester' ? formData.duration_years * 2 + ' semesters' : formData.duration_years + ' years'} automatically
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingCourse(null); setError(''); }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {loading ? 'Saving...' : (editingCourse ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
